import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import {
  getWorkspaceModules,
  isModuleEnabled,
  WORKSPACE_MODULES_UPDATED_EVENT,
} from '../../lib/workspaceModulesService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { FEATURE_FLAGS, FEATURE_FLAGS_UPDATED_EVENT, isFeatureEnabled } from '../../lib/featureFlags';
import { useIsElevationTeam } from '../../lib/elevationAccess';
/**
 * Full-width footer for the prioritization shell (content column only — below main).
 * Uses the same raised surface tokens as the top bar so dark mode is never a white strip.
 * The team-home link (/home, internal material) is drawn only for the team
 * (Wes and Harry; lib/elevationAccess.ts), never for customers.
 */
export function PrioritizationAppFooter() {
  const [version, setVersion] = useState(0);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const elevationTeam = useIsElevationTeam();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        if (!cancelled) setFeatures({});
        return;
      }
      getWorkspaceModules(workspaceId).then(({ data }) => {
        if (!cancelled) setFeatures(data);
      });
    };
    load();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    };
  }, []);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  // version is read here so any flag-storage change triggers a re-render
  const showTeamHome =
    elevationTeam &&
    isModuleEnabled(features, 'elevation_platform') &&
    isFeatureEnabled(FEATURE_FLAGS.ELEVATION_NAV) &&
    version >= 0; // keeps version in scope without a lint-problematic useMemo dep

  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto flex flex-col gap-3 border-t border-[var(--rule)] bg-[var(--surface)] px-8 py-3 text-sm transition-colors duration-200 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <nav
        className="flex flex-wrap items-center gap-x-8 gap-y-2"
        aria-label="Footer"
      >
        {showTeamHome && (
          <Link
            to="/home"
            className="inline-flex items-center gap-2 font-medium text-[var(--text-ter)] transition-colors hover:text-[var(--text-ter)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)]"
          >
            <ExternalLink className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
            Team home
          </Link>
        )}
        <Link
          to="/prioritization/documentation"
          className="text-[var(--text-sec)] transition-colors hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
        >
          Documentation
        </Link>
        {/* Pointed at the actual guide again. This linked to
            /prioritization/documentation — the same target as the Documentation
            link beside it — so the User Guide was unreachable from the nav even
            though its route existed. */}
        <Link
          to="/prioritization/documentation/user-guide"
          className="text-[var(--text-sec)] transition-colors hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
        >
          User guide
        </Link>
        <Link
          to="/prioritization/documentation/api"
          className="text-[var(--text-sec)] transition-colors hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
        >
          API reference
        </Link>
        <a
          href="https://priorities-ai-marketing.vercel.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--text-sec)] transition-colors hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
        >
          priorities.ai
        </a>
      </nav>

      <p className="shrink-0 text-xs text-[var(--text-ter)] sm:text-right dark:text-[var(--text-ter)]">
        © {year} Peak Priorities. All rights reserved.
      </p>
    </footer>
  );
}
